/**
 * Notice Consent Banner
 *
 * Shopify's native cookie banner only renders in consent-required
 * (opt-out-by-default) regions -- EU, Quebec, etc. -- because Shopify ties
 * banner visibility to blocking: showing it anywhere also blocks that region
 * by default (confirmed 2026-07-13; there is no way to decouple the two).
 * So opt-in-by-default regions (e.g. US) get no native banner at all, even
 * though a banner should still greet every first-time visitor there too.
 *
 * This fills that gap with a lightweight NOTICE banner -- Accept only, no
 * Decline, no blocking (nothing needs to be gated; tracking is already
 * allowed by default in these regions). It shows only when Shopify's own
 * API confirms this is an allowed-by-default region, mirroring the same
 * analyticsProcessingAllowed() signal opt-in-cookie-consent.js already uses
 * for Decline-button gating -- no separate country list to maintain.
 *
 * Dismissal = shown once, ever (per browser, until cookies/localStorage are
 * cleared) -- navigating away counts as implicit acceptance, not just an
 * explicit Accept click. This is legally fine specifically because nothing
 * is gated by it: tracking in these regions is already allowed by default
 * regardless of any click, so there's no consent decision being relied on
 * either way (unlike Shopify's native banner in blocking regions, where
 * continued browsing is NOT valid consent under GDPR and an explicit action
 * is required -- that's Shopify's own logic, untouched by this file).
 *
 * Because navigating away counts as acceptance, Shopify's own consent record
 * is set to reflect that at DISPLAY time, not only on an explicit Accept click
 * -- otherwise currentVisitorConsent() would stay blank/"undetermined" forever
 * for anyone who never clicks, even though we're treating them as opted in.
 * Still skips that call entirely if the visitor already opted out via
 * GPC/server tag, so an unrelated cookie notice can never silently override
 * a real opt-out with blank-vs-opted-in ambiguity either.
 *
 * Two deliberate narrowings of that implied-consent write:
 *
 *   1. sale_of_data is sent ONLY on an explicit Accept/close click. Implied
 *      consent to cookies/analytics is defensible where they're allowed by
 *      default; asserting "you may sell/share my personal information" on
 *      behalf of someone who never clicked is not, since that's precisely the
 *      right US state privacy laws reserve to the individual. Omitting the key
 *      leaves Shopify's regional default in place instead of overriding it.
 *   2. The shown-once flag is set a few seconds after display (or on pagehide,
 *      or on click) rather than the instant the banner paints, so one
 *      fast-bounce pageview can't silently spend the visitor's only notice.
 *      The consent record and the shown-once flag are now separate concerns.
 */
(function () {
  'use strict';

  var DISMISSED_KEY = 'notice_consent_dismissed';
  var WITNESSED_KEY = 'consent_decision_witnessed';
  var CONSENT_REGION_KEY = 'consent_required_region_seen';
  // 40 polls x 500ms = ~20s, matching opt-in-cookie-consent.js. The old 10s
  // budget could expire without ever landing CONFIRMATIONS_REQUIRED consecutive
  // allowed reads on a slow connection (getRegion() flickering empty resets the
  // counter), leaving a first-time US visitor with no banner at all.
  var MAX_ATTEMPTS = 40;
  var CONFIRMATIONS_REQUIRED = 2;
  // How long to keep watching for a late native banner after we've shown ours.
  // Shopify injects well inside this; without a stop the observer would run a
  // querySelector on every DOM mutation for the life of the page (in the US,
  // where the native banner never comes, that means forever).
  var LATE_BANNER_WATCH_MS = 10000;
  // The consent-required lock is a cache of a region reading, not a permanent
  // fact about the browser -- expire it so someone who once loaded the site
  // from the EU isn't locked out of the US notice banner for good.
  var CONSENT_REGION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  // Grace period before a displayed-but-unclicked banner counts as dismissed,
  // so a fast bounce doesn't consume the visitor's only cookie notice. Also
  // committed early on pagehide (they saw it, then left).
  var DISMISS_GRACE_MS = 4000;

  function debugEnabled() {
    try { return sessionStorage.getItem('consent_debug') === '1'; } catch (e) { return false; }
  }

  document.addEventListener('visitorConsentCollected', function () {
    try { localStorage.setItem(WITNESSED_KEY, '1'); } catch (e) {}
  });

  function hasWitnessedDecision() {
    try {
      return localStorage.getItem(WITNESSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  // One-time repair for locks written by the broken saleOfDataRegion() gate.
  //
  // That gate ran for every CCPA/CPRA opt-out state (CA, UT, CO, VA...) and
  // wrote a 30-day consent-required lock before suppressing the banner. Every
  // visitor who loaded the broken build from one of those states now carries a
  // lock that would keep suppressing the notice for up to 30 days even after
  // the gate is removed. Clearing it here is safe: the lock is only ever a
  // cache of a region reading, and a genuine consent-required region rewrites
  // it within one evaluate() pass (native banner present -> markConsentRequired
  // -RegionSeen). The stamp key makes this run exactly once per browser.
  var REGION_LOCK_REPAIR_KEY = 'consent_region_lock_repaired_v2';
  try {
    if (localStorage.getItem(REGION_LOCK_REPAIR_KEY) !== '1') {
      localStorage.removeItem(CONSENT_REGION_KEY);
      localStorage.setItem(REGION_LOCK_REPAIR_KEY, '1');
    }
  } catch (e) {}

  function markConsentRequiredRegion() {
    try {
      localStorage.setItem(CONSENT_REGION_KEY, String(Date.now()));
    } catch (e) {}
  }

  function isConsentRequiredRegion() {
    try {
      var raw = localStorage.getItem(CONSENT_REGION_KEY);
      if (!raw) return false;
      // Legacy value written before the TTL existed: treat as set-but-undated
      // and re-stamp it so it starts aging from now rather than never expiring.
      if (raw === '1') {
        markConsentRequiredRegion();
        return true;
      }
      var stamped = parseInt(raw, 10);
      if (!stamped) return false;
      if (Date.now() - stamped > CONSENT_REGION_TTL_MS) {
        localStorage.removeItem(CONSENT_REGION_KEY);
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  function isDismissed() {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDismissed() {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch (e) {
    }
  }

  // In a theme preview Shopify can render its banner even where its own
  // region flag (shouldShowBanner) says it is not needed. That preview-only
  // banner must not count as "consent-required region", or previews never
  // show this notice. Never true on the live theme (Shopify.previewMode is
  // unset there). Keep in step with the same checks in
  // opt-in-cookie-consent.js.
  function inPreview() {
    var s = window.Shopify;
    return !!s && s.previewMode === true &&
      window.location.search.indexOf('preview_privacy_banner=1') === -1;
  }

  function privacyApiReady() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    return !!cp && typeof cp.shouldShowBanner === 'function';
  }

  function previewOnlyBanner() {
    if (!inPreview() || !privacyApiReady()) return false;
    try {
      return window.Shopify.customerPrivacy.shouldShowBanner() === false;
    } catch (e) {
      return false;
    }
  }

  function nativeBannerPresent() {
    return !!document.querySelector('#shopify-pc__banner, .shopify-pc__banner') && !previewOnlyBanner();
  }

  function hasConsentDecision(cp) {
    try {
      if (typeof cp.currentVisitorConsent !== 'function') return false;
      var c = cp.currentVisitorConsent();
      if (!c || typeof c !== 'object') return false;
      return c.marketing === 'yes' || c.marketing === 'no' ||
             c.analytics === 'yes' || c.analytics === 'no';
    } catch (e) {
      return false;
    }
  }

  function visitorHasOptedOut() {
    try { if (navigator.globalPrivacyControl === true) return true; } catch (e) {}
    if (window.PrivacyOptOut && window.PrivacyOptOut.isOptedOut === true) return true;
    try {
      var stored = localStorage.getItem('privacy_opt_out');
      if (stored && JSON.parse(stored).optedOut === true) return true;
    } catch (e) {}
    return false;
  }

  /**
   * Write the consent record for an allowed-by-default region.
   *
   * @param {boolean} explicit - true when the visitor actually clicked
   *   Accept/close; false when we're recording implied consent at display time.
   *
   * sale_of_data is ONLY sent on an explicit click. Implied "you kept browsing"
   * consent is a reasonable basis for cookies/analytics in an opt-in-by-default
   * region, but "yes, you may sell/share my personal information" is the exact
   * right US state privacy laws give people to withhold -- so we don't assert it
   * on the visitor's behalf from a banner they may never have looked at. Leaving
   * it unset keeps Shopify's own regional default, rather than overriding it.
   */
  function recordAcceptance(explicit) {
    if (visitorHasOptedOut()) return;

    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.setTrackingConsent !== 'function') return;

    var consent = {
      marketing: true,
      analytics: true,
      preferences: true
    };
    if (explicit === true) consent.sale_of_data = true;

    try {
      cp.setTrackingConsent(consent, function (result) {
        // The "navigating away counts as acceptance" model depends on this write
        // actually landing, so a failure must not stay invisible.
        if (result && result.error) {
          console.warn('[notice-consent-banner] setTrackingConsent reported an error:', result.error);
          return;
        }
        if (debugEnabled()) {
          console.log('[notice-consent-banner] consent recorded', {
            explicit: explicit === true,
            sent: consent
          });
        }
      });
    } catch (e) {
      console.warn('[notice-consent-banner] setTrackingConsent threw:', e);
    }
  }

  var watchdog = null;
  // Set when the late-native-banner watchdog retracts this notice. Once
  // retracted, the deferred/pagehide dismissal below must NOT write the
  // shown-once flag back -- the whole point of retracting is to let this
  // visitor see the correct (native) banner on their next load.
  var retracted = false;

  function stopWatchdog() {
    if (watchdog) {
      watchdog.disconnect();
      watchdog = null;
    }
  }

  function watchForLateNativeBanner(banner) {
    if (typeof MutationObserver !== 'function') return;
    watchdog = new MutationObserver(function () {
      if (!nativeBannerPresent()) return;
      banner.style.display = 'none';
      retracted = true;
      markConsentRequiredRegion(); // native banner really did render here
      try { localStorage.removeItem(DISMISSED_KEY); } catch (e) {}
      // warn, not error: this is a handled recovery, and routing it to
      // console.error makes it show up as a genuine fault in error monitoring.
      console.warn('[notice-consent-banner] Shopify\'s native banner appeared ' +
        'after this notice banner was already shown -- retracted. This region ' +
        'was likely misread as allowed-by-default before Shopify\'s API finished ' +
        'resolving the true (consent-required) value.');
      stopWatchdog();
    });
    watchdog.observe(document.documentElement, { childList: true, subtree: true });

    // Stop watching once Shopify's injection window has passed. Otherwise, in
    // the common case where no native banner ever arrives, this observer keeps
    // running a querySelector on every DOM mutation for the life of the page.
    setTimeout(stopWatchdog, LATE_BANNER_WATCH_MS);
  }

  function showBanner() {
    var banner = document.getElementById('notice-consent-banner');
    if (!banner) return;
    banner.style.display = 'block';

    // Consent record is still written at DISPLAY time -- that's the documented
    // model above: in an allowed-by-default region, continuing to browse IS the
    // acceptance, so currentVisitorConsent() must reflect it even for a visitor
    // who never clicks. (Minus sale_of_data; see recordAcceptance.)
    recordAcceptance(false);

    // But "never show this again" is now tied to the banner actually having been
    // SEEN, not merely painted. Marking dismissed at display time meant a single
    // fast-bounce pageview spent the visitor's one and only cookie notice --
    // possibly before it even finished rendering. The banner is shown once ever;
    // it should at least be a pageview where it was on screen for a moment.
    function commitDismissal() {
      if (retracted) return; // watchdog took this banner back; leave the flag clear
      setDismissed();
    }

    var dismissTimer = setTimeout(commitDismissal, DISMISS_GRACE_MS);

    watchForLateNativeBanner(banner);

    function onAccept() {
      banner.style.display = 'none';
      clearTimeout(dismissTimer);
      commitDismissal();
      stopWatchdog();
      recordAcceptance(true); // explicit click -- includes sale_of_data
    }

    var acceptBtn = document.getElementById('notice-consent-accept');
    if (acceptBtn) acceptBtn.addEventListener('click', onAccept);

    var closeBtn = document.getElementById('notice-consent-close');
    if (closeBtn) closeBtn.addEventListener('click', onAccept);

    // Opens Shopify's native preferences modal -- deliberately NOT wired to
    // onAccept. This is a separate action from Continue/close: a visitor
    // inspecting their options hasn't stated a preference yet, so opening the
    // modal must not itself dismiss the bar or record any consent. Self-
    // contained (not window.osborneOpenCookiePreferences from
    // footer-privacy-bar.liquid) because the footer renders far later in
    // <body> and this bar can be interacted with before that script runs.
    var manageBtn = document.getElementById('notice-consent-manage');
    if (manageBtn) {
      manageBtn.addEventListener('click', function () {
        if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
          window.privacyBanner.showPreferences();
          return;
        }
        if (window.Shopify && window.Shopify.customerPrivacy &&
            typeof window.Shopify.customerPrivacy.showPreferences === 'function') {
          window.Shopify.customerPrivacy.showPreferences();
        }
      });
    }

    // A visitor who reads the notice and navigates away without clicking has
    // still seen it -- honor the implied dismissal even if they leave before the
    // grace period is up.
    window.addEventListener('pagehide', function () {
      clearTimeout(dismissTimer);
      commitDismissal();
    }, { once: true });
  }

  var consecutiveAllowed = 0;

  function evaluate(attempt) {
    // In a preview, wait for the Customer Privacy API before reading anything:
    // without it the region flag is unknown and a stale lock would end polling.
    if (inPreview() && !privacyApiReady()) return false;
    // A lock written by an earlier preview load, before the check above existed.
    if (previewOnlyBanner()) {
      try { localStorage.removeItem(CONSENT_REGION_KEY); } catch (e) {}
    }
    if (nativeBannerPresent()) {
      markConsentRequiredRegion();
      return true; // native banner is handling this region
    }
    if (isDismissed() || hasWitnessedDecision() || isConsentRequiredRegion()) return true;

    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return false;

    var region;
    try {
      region = typeof cp.getRegion === 'function' ? cp.getRegion() : null;
    } catch (e) {
      return false;
    }
    if (typeof region !== 'string' || region === '') {
      consecutiveAllowed = 0;
      return false;
    }

    // Negative region gate that does NOT depend on the native banner having
    // injected yet, closing the window in which analyticsProcessingAllowed()
    // can briefly read true in the EU/Quebec and flash this banner.
    //
    // Do NOT gate on saleOfDataRegion() here. It does not mean "consent
    // required" -- it means "this region has do-not-sell/share OPT-OUT rights"
    // (CCPA/CPRA: California, Utah, Colorado, Virginia...). It returns true for
    // Utah, an allowed-by-default region that SHOULD get this notice banner, so
    // gating on it suppressed the banner across most of the US and wrote a
    // 30-day consent-required lock on the way out. Confirmed against a live
    // US-UT session: region "USUT", saleOfDataRegion true, analyticsAllowed
    // true, no native banner in the DOM.
    //
    // The reliable consent-required signal is the native banner itself
    // (nativeBannerPresent(), checked at the top of evaluate) plus the
    // consent-required-region class opt-in-cookie-consent.js sets from that
    // same DOM-presence check.
    if (document.documentElement.classList.contains('consent-required-region')) {
      markConsentRequiredRegion();
      consecutiveAllowed = 0;
      return true;
    }

    var allowed;
    try {
      allowed = cp.analyticsProcessingAllowed();
    } catch (e) {
      return false;
    }
    if (typeof allowed !== 'boolean') return false;

    if (allowed !== true) {
      consecutiveAllowed = 0;
      return false;
    }

    if (hasConsentDecision(cp)) {
      consecutiveAllowed = 0;
      return true;
    }

    consecutiveAllowed += 1;
    if (consecutiveAllowed < CONFIRMATIONS_REQUIRED) return false;

    showBanner();
    return true;
  }

  if (!evaluate(0)) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (evaluate(attempts) || attempts >= MAX_ATTEMPTS) {
        clearInterval(timer);
      }
    }, 500);
  }
})();
